/**
 * Wybór motywu.
 *
 * Rozdzielone są dwie rzeczy, które łatwo pomylić: **preferencja**
 * (`auto`/`jasny`/`ciemny`, to, co wybrał człowiek) i **motyw**
 * (`jasny`/`ciemny`, to, co widać). W `localStorage` leży preferencja,
 * na `<html data-motyw>` ląduje motyw.
 *
 * Gdyby atrybut niósł preferencję, CSS musiałby powtórzyć całą jasną paletę
 * drugi raz w `@media (prefers-color-scheme)` — a dwie kopie dziesięciu
 * wartości rozjeżdżają się przy pierwszej korekcie odcienia.
 */

import { useEffect, useState } from "react";
import { AKCENTY, parsujAkcent, type Akcent } from "@gierki/shared";
import { api } from "./api.ts";

export { AKCENTY, parsujAkcent, type Akcent };

export type Preferencja = "auto" | "jasny" | "ciemny";
export type Motyw = "jasny" | "ciemny";

export const KLUCZ_MOTYWU = "gierki:motyw";
export const KLUCZ_AKCENTU = "gierki:akcent";

/**
 * Kolor paska stanu Androida, dokładnie równy tokenowi `tlo` obu motywów.
 * Wartości policzone z `oklch` — wcześniej stała tu przybliżona barwa
 * Tailwinda i pasek był widocznie jaśniejszy od aplikacji.
 */
export const KOLOR_PASKA: Record<Motyw, string> = {
  jasny: "#f6f9fc",
  ciemny: "#0a1018",
};

export type Magazyn = {
  getItem(klucz: string): string | null;
  setItem(klucz: string, wartosc: string): void;
};

export function rozwiazMotyw(pref: Preferencja, systemJasny: boolean): Motyw {
  if (pref === "auto") return systemJasny ? "jasny" : "ciemny";
  return pref;
}

export function parsujPreferencje(surowa: string | null): Preferencja {
  return surowa === "jasny" || surowa === "ciemny" || surowa === "auto" ? surowa : "auto";
}

/**
 * Domyślny magazyn to `localStorage`, ale tylko jeśli istnieje.
 *
 * Testy chodzą w środowisku `node`, gdzie go nie ma.
 */
function domyslnyMagazyn(): Magazyn | null {
  return typeof localStorage === "undefined" ? null : localStorage;
}

export function czytajPreferencje(magazyn: Magazyn | null = domyslnyMagazyn()): Preferencja {
  try {
    return parsujPreferencje(magazyn?.getItem(KLUCZ_MOTYWU) ?? null);
  } catch {
    // Prywatny tryb przeglądarki bywa szczelny. Motyw to ozdoba, nie powód
    // do białego ekranu.
    return "auto";
  }
}

export function zapiszPreferencje(
  pref: Preferencja,
  magazyn: Magazyn | null = domyslnyMagazyn(),
): void {
  try {
    magazyn?.setItem(KLUCZ_MOTYWU, pref);
  } catch {
    // Wybór nie przeżyje restartu, ale bieżąca sesja działa.
  }
}

export function czytajAkcent(magazyn: Magazyn | null = domyslnyMagazyn()): Akcent {
  try {
    return parsujAkcent(magazyn?.getItem(KLUCZ_AKCENTU) ?? null);
  } catch {
    return "zielony";
  }
}

export function zapiszAkcent(akcent: Akcent, magazyn: Magazyn | null = domyslnyMagazyn()): void {
  try {
    magazyn?.setItem(KLUCZ_AKCENTU, akcent);
  } catch {
    // Jak przy motywie: bieżąca sesja działa, wybór nie przeżyje restartu.
  }
}

/**
 * Kolor przyszedł z konta — zapisz go w pamięci podręcznej i przestaw wybór.
 *
 * `localStorage` przestał być źródłem prawdy, ale zostaje: kolor trzeba nałożyć
 * przed pierwszym renderem, a odpowiedź `/api/auth/me` przychodzi później.
 * Bez cache'u aplikacja mrugałaby domyślną zielenią przy każdym otwarciu.
 *
 * Malowanie DOM-u zostawiamy efektowi w `useMotyw` — ta funkcja nie dotyka
 * `document`. Inaczej nie dałoby się jej przetestować: `vitest` biegnie
 * w `environment: "node"` i `document` tam nie istnieje.
 */
export function zsynchronizujAkcent(
  zSerwera: string | null,
  magazyn: Magazyn | null = domyslnyMagazyn(),
): Akcent {
  const akcent = parsujAkcent(zSerwera);
  zapiszAkcent(akcent, magazyn);
  ustawWybor({ akcent });
  return akcent;
}

export function systemJasny(): boolean {
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-color-scheme: light)").matches;
}

export function zastosujMotyw(motyw: Motyw): void {
  document.documentElement.dataset.motyw = motyw;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", KOLOR_PASKA[motyw]);
}

export function zastosujAkcent(akcent: Akcent): void {
  document.documentElement.dataset.akcent = akcent;
}

/**
 * Wybór żyje w module, nie w komponencie.
 *
 * `useMotyw` woła i `App` (żeby utrzymać zgodność z systemem przez całe życie
 * aplikacji), i ekran Opcji (żeby pokazać zaznaczenie). Dwie kopie `useState`
 * rozjechałyby się przy pierwszym kliknięciu: ekran wiedziałby o zmianie,
 * `App` nadal trzymałby starą wartość i przywrócił ją przy swoim renderze.
 */
let wybor = { pref: czytajPreferencje(), akcent: czytajAkcent() };
const sluchacze = new Set<() => void>();

function ustawWybor(zmiana: Partial<typeof wybor>): void {
  wybor = { ...wybor, ...zmiana };
  for (const sluchacz of sluchacze) sluchacz();
}

export function useMotyw() {
  const [, odswiez] = useState(0);
  const [jasnySystem, setJasnySystem] = useState(systemJasny);

  useEffect(() => {
    const sluchacz = () => odswiez((n) => n + 1);
    sluchacze.add(sluchacz);
    return () => void sluchacze.delete(sluchacz);
  }, []);

  // Telefon wchodzi w tryb nocny o zmierzchu, często w środku partii — bez
  // nasłuchu motyw przestawiłby się dopiero przy następnym otwarciu aplikacji.
  useEffect(() => {
    if (typeof matchMedia === "undefined") return;
    const zapytanie = matchMedia("(prefers-color-scheme: light)");
    const nasluch = (e: MediaQueryListEvent) => setJasnySystem(e.matches);
    zapytanie.addEventListener("change", nasluch);
    return () => zapytanie.removeEventListener("change", nasluch);
  }, []);

  const motyw = rozwiazMotyw(wybor.pref, jasnySystem);
  const akcent = wybor.akcent;
  useEffect(() => zastosujMotyw(motyw), [motyw]);
  useEffect(() => zastosujAkcent(akcent), [akcent]);

  return {
    pref: wybor.pref,
    motyw,
    akcent,
    ustaw: (nowa: Preferencja) => {
      zapiszPreferencje(nowa);
      ustawWybor({ pref: nowa });
    },
    ustawAkcent: (nowy: Akcent) => {
      // Malujemy od razu, zapis na konto idzie w tle: czekanie na odpowiedź
      // serwera przed zmianą koloru wyglądałoby jak zacięty przycisk.
      zapiszAkcent(nowy);
      ustawWybor({ akcent: nowy });
      void api.ustawKolor(nowy).catch(() => {
        /* offline: kolor zostaje lokalnie, konto dogoni przy następnej zmianie */
      });
    },
  };
}
